import { useEffect, useState, VFC } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Box, Button, Card } from "@mui/material";
import ButtonArea from "../common/ButtonArea";
import { BackButton } from "../common/BackButton";
import {
  ContractDetailRequest,
  ContractDetailResponse,
} from "./api/ContractForm";
import { ContractService } from "./api/ContractService";
import { AxiosResponse } from "axios";
import Constants from "../../utils/Constants";
import { ResultDialog, ResultResponse } from "../common/dialog/ResultDialog";
import ContractDetailView from "../common/detailview/ContractDetailView";
import { ApplicationProtectedRouting } from "../../Routing";
import { ContractEditMode } from "../../entity/ContractEditMode";
import { CONTRACT_STATUS } from "../../entity/DeviceUser";
import { CONTRACT_TRIAL } from "../../entity/Contract";
import { Loading } from "../common/Loading";

const ContractDetail: VFC = () => {
  const PAGE_NAME = "契約詳細";
  const navigate = useNavigate();

  /***************************************************************
   * State管理
   **************************************************************/
  // 共通ローディング
  const [isLoading, setIsLoading] = useState(false);

  // 契約リクエスト
  const [request, setRequest] = useState<ContractDetailRequest>(
    new ContractDetailRequest()
  );

  // 契約情報
  const [contract, setContract] = useState<ContractDetailResponse>(
    new ContractDetailResponse()
  );

  // 編集ボタン(活性・非活性)
  const [isDisable, setIsDisable] = useState(false);

  // 共通ダイアログ(ステータス)
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // 処理結果
  const [resultResponse, setResultResponse] = useState<ResultResponse>({
    status: "",
    code: "",
    displayMessage: "",
    result: [],
    errorMessages: [],
  });

  /***************************************************************
   * 共通処理
   **************************************************************/
  // 処理結果ダイアログ(CLOSE)
  const handleResultDialogClose = () => {
    setIsDialogOpen(false);
  };
  // 処理結果を設定
  const setStateResultResponse = (
    status: string,
    code: string,
    displayMessage: string,
    result: string
  ) => {
    // TODO setterを利用するよう変更
    resultResponse.status = status;
    resultResponse.code = code;
    resultResponse.displayMessage = displayMessage;
    resultResponse.result = [result];

    setIsDialogOpen(true);
    setIsDisable(true);
    setResultResponse(resultResponse);
  };

  /***************************************************************
   * 初期表示処理
   **************************************************************/
  useEffect(() => {
    // 利用者IDを設定
    // TODO setRequestを使用すること
    request.id = sessionStorage.getItem("CONTRACT_ID") ?? "";
    setRequest(request);
    // 利用者情報取得
    findByContractId();
  }, []);

  // 利用者詳細取得API
  const findByContractId = () => {
    const service = new ContractService();
    if ("" !== request.id) {
      setIsLoading(true);
      service
        .findByContractId(request.id)
        .then((response: AxiosResponse) => {
          if (Constants.ERR_STATUS_OK === response.data.status) {
            const result = response.data.result as ContractDetailResponse;
            result.id = request.id;
            result.status = CONTRACT_STATUS[result.status];
            result.trial = CONTRACT_TRIAL[result.trial];
            setContract(response.data.result);
          } else {
            setIsDialogOpen(true);
            setIsDisable(true);
            setResultResponse(response.data);
          }
        })
        .catch((err) => {
          setStateResultResponse(
            Constants.ERR_STATUS_NG,
            err.response.status,
            err.response.statusText,
            Constants.E_MSG_SYSTEM_ERR
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setStateResultResponse(
        Constants.ERR_STATUS_NG,
        Constants.ERR_CODE_404,
        Constants.T_MSG_NO_DATA,
        Constants.W_MSG_NO_DATA
      );
    }
  };

  /***************************************************************
   * 編集
   **************************************************************/
  // 編集ボタン押下
  const editButtonClick = (id: string) => {
    // 利用者編集/新規追加画面へ遷移する
    sessionStorage.setItem("CONTRACT_ID", id);
    navigate(ApplicationProtectedRouting.contractEdit.path(id), {state: {
      mode: ContractEditMode.EDIT,
    }});
  };

  /***************************************************************
   * 複製
   **************************************************************/
  // 複製ボタン押下
  const duplicateButtonClick = (id: string) => {
    sessionStorage.setItem("CONTRACT_ID", id);
    navigate(ApplicationProtectedRouting.contractEdit.path(id), {state: {
      mode: ContractEditMode.DUPLICATE,
    }})
  }

  return (
    <>
      <Box>
        {/* ボタンの配置 */}
        <ButtonArea pageName={PAGE_NAME}>
          <BackButton />
          {/* ボタン1つ分の空白 */}
          <Button
            disabled={true}
          ></Button>
          <Button
            variant="contained"
            color="primary"
            disabled={isDisable}
            onClick={() => duplicateButtonClick(request.id)}
          >
            複製
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={isDisable}
            onClick={() => editButtonClick(request.id)}
          >
            編集
          </Button>
        </ButtonArea>

        {/* 利用者情報 */}
        <Box padding={1}>
          <Card>
            <Box sx={{ p: 3 }}>
              <Grid container direction="column" spacing={2}>
                <ContractDetailView contract={contract} />
              </Grid>
            </Box>
          </Card>
        </Box>
      </Box>
      {/* 処理結果ダイアログ */}
      <ResultDialog
        isDialogOpen={isDialogOpen}
        resultResponse={resultResponse}
        handler={handleResultDialogClose}
      />
      
      {/* 処理ローディング */}
      <Loading isLoading={isLoading} />
    </>
  );
};

export default ContractDetail;
